import { Competency_Map_Status, CompetencyMapsQuery, useDeleteCompetencyMapMutation } from "@generated/graphql";
import { MoreHoriz } from "@mui/icons-material";
import { Button, IconButton, Popover, Stack } from "@mui/material";
import CircularLoading from "components/CircularLoading";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import useDownloadCompetencyMap from "hooks/useDownloadCompetencyMap";
import { MouseEvent, useContext, useState } from "react";
import typography from "constants/typography.constants";
import useUpdateCompetencyMapStatus from "hooks/useUpdateCompetencyMapStatus";
import { RefetchCompetencyMaps } from "../types";

interface ActionButtonWithPopoperProps {
  competencyMap: NonNullable<CompetencyMapsQuery["competencyMaps"]["data"]>[number];
  refetch: RefetchCompetencyMaps;
}

const ActionButtonWithPopoper = ({ competencyMap, refetch }: ActionButtonWithPopoperProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { addToast } = useContext(ToastContext);
  const { handleOpen: handleOpenConfirmModal } = useContext(ConfirmModalContext);

  const handlePopoverOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handlePopoverClose = (e: MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const [deleteMap] = useDeleteCompetencyMapMutation();
  const { isLoading: isDownloading, getCompetencyMapFile } = useDownloadCompetencyMap();
  const handleDownloadXlsxMap = () => getCompetencyMapFile(competencyMap);

  const onArchiveCompetencyMap = () => {
    refetch().catch((e) => parseApolloError(e, { addToast }));
  };
  const { handleArchiveCompetencyMap, handleUpdateCompetencyMapStatus } = useUpdateCompetencyMapStatus({
    competencyMap,
    onArchiveCompetencyMap,
  });

  const handleDeleteMap = async () => {
    await deleteMap({
      variables: {
        competencyMapId: competencyMap.id,
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: typography.competencyMap.deleted });
        refetch().catch((e) => parseApolloError(e, { addToast }));
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const handleOpenConfirmDeleteMapModal = () => {
    handleOpenConfirmModal({
      title: "Вы уверены, что хотите удалить карту компетенций?",
      actionButtonText: "Удалить",
      handleClick: handleDeleteMap,
    });
  };

  const handleOpenConfirmRecoverModal = () => {
    handleOpenConfirmModal({
      title: typography.competencyMap.recover.title,
      actionButtonText: typography.competencyMap.recover.confirm,
      handleClick: () => handleUpdateCompetencyMapStatus(Competency_Map_Status.Draft),
    });
  };

  const archiveMapActions = [
    {
      id: 1,
      title: typography.competencyMap.recover.confirm,
      onClick: handleOpenConfirmRecoverModal,
    },
    {
      id: 2,
      title: "Удалить",
      onClick: handleOpenConfirmDeleteMapModal,
    },
  ];

  const activeMapActions = [
    {
      id: 1,
      title: typography.competencyMap.archive.buttonTitle,
      onClick: handleArchiveCompetencyMap,
    },
    {
      id: 2,
      title: "Удалить",
      onClick: handleOpenConfirmDeleteMapModal,
    },
    {
      id: 3,
      title: "Скачать карту компетенций",
      onClick: handleDownloadXlsxMap,
    },
  ];

  if (isDownloading) {
    return (
      <CircularLoading
        size={24}
        sx={{ p: 0, display: "block" }}
      />
    );
  }

  return (
    <>
      <IconButton onClick={handlePopoverOpen}>
        <MoreHoriz />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus>
        <Stack>
          {(competencyMap.status === Competency_Map_Status.Archive ? archiveMapActions : activeMapActions).map(
            (action) => (
              <Button
                key={action.id}
                variant='text'
                onClick={(e) => {
                  e.stopPropagation();
                  handlePopoverClose(e);
                  action.onClick();
                }}>
                {action.title}
              </Button>
            )
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default ActionButtonWithPopoper;
